import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

type IconButtonVariant = "default" | "danger" | "success" | "brand";

const variantClass: Record<IconButtonVariant, string> = {
  default:
    "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900",
  brand:
    "border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100",
  success:
    "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100",
  danger:
    "border-red-200 bg-white text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700",
};

type IconButtonSize = "md" | "sm";

const sizeClass: Record<IconButtonSize, string> = {
  md: "h-9 w-9 rounded-lg",
  sm: "h-8 w-8 rounded-md",
};

const baseClass =
  "inline-flex items-center justify-center border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  children: ReactNode;
}

export function IconButton({
  title,
  variant = "default",
  size = "md",
  className = "",
  children,
  ...props
}: IconButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`${baseClass} ${sizeClass[size]} ${variantClass[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

interface IconLinkProps extends Omit<LinkProps, "title"> {
  title: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  external?: boolean;
  children: ReactNode;
}

export function IconLink({
  title,
  variant = "default",
  size = "md",
  external,
  className = "",
  children,
  ...props
}: IconLinkProps) {
  return (
    <Link
      title={title}
      aria-label={title}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={`${baseClass} ${sizeClass[size]} ${variantClass[variant]} ${className}`}
      {...props}
    >
      {children}
    </Link>
  );
}
